import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import api from "../../api";
import useFeatures from "../../hooks/useFeatures";

// Settings → Clinical & Safety. Each switch appears only once its practice
// feature is on: who may read clinical notes (clinical/access/clinical_access.py,
// "clinical_access") and whether a mental-health session needs a risk
// assessment before it can be completed (clinical/safety/risk.py, "risk").
const SWITCHES = [
  {
    key: "admins_can_read_clinical", feature: "clinical_access", title: "Clinical notes privacy",
    help: "Clinical notes are open only to each patient's care team and their supervisors. Admins and operations staff see appointments and billing, not notes.",
    label: "Let admins and operations staff read clinical notes too",
    on: "Admins can now read clinical notes", off: "Clinical notes are limited to each patient's care team",
  },
  {
    key: "measure_results_to_patients", feature: "measures", title: "Questionnaire results for patients",
    help: "Off (recommended): after a questionnaire the patient sees only that their answers went to their clinician, who will go through them together - a severity label read alone can be alarming. On: they also see a plain-language summary such as \"your answers suggest mild symptoms\". The crisis message for risky answers always shows.",
    label: "Show patients a plain-language summary of their questionnaire result",
    on: "Patients will see a summary of their result", off: "Patients will only see that their clinician will go through it",
  },
  {
    key: "note_review_required", feature: "note_signing", title: "Session note review",
    help: "Clinicians sign their notes, which locks them; later changes are added as addenda. With review on, a signed note goes to the clinician's supervisor to approve. Clinicians without a supervisor sign without review.",
    label: "Supervisors review signed notes before they're final",
    on: "Signed notes now go to supervisors for review", off: "Signed notes are final straight away",
  },
  {
    key: "risk_required_for_mh", feature: "risk", title: "Risk assessment",
    help: "A short risk assessment (suicidal ideation, self-harm, risk to others, overall level) recorded in the session notes. A rise to moderate or high risk alerts the care team.",
    label: "Require a risk assessment before a mental-health session can be completed",
    on: "Risk assessment is now required", off: "Risk assessment is now optional",
  },
];

export const hasClinicalSettings = (features) => SWITCHES.some((s) => features.has(s.feature)) || features.has("crisis") || features.has("note_templates") || features.has("measures") || features.has("treatment_plan");

export default function ClinicalPrivacyCard() {
  const features = useFeatures();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);

  let isOrgAdmin = false;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    isOrgAdmin = (jwtDecode(auth.token)?.sub?.role || "").toLowerCase() === "admin";
  } catch { isOrgAdmin = false; }

  useEffect(() => {
    if (!hasClinicalSettings(features)) return;
    api.getGeneralSettings().then((r) => setSettings(r.data || {})).catch(() => setSettings({}));
  }, [features.ready]); // eslint-disable-line

  const visible = SWITCHES.filter((s) => features.has(s.feature));
  if (!features.ready) return null;
  if (visible.length === 0 && !features.has("crisis") && !features.has("treatment_plan")) {
    if (features.has("note_templates")) return null;
    return <p className="text-sm text-gray-500">No clinical features are switched on for your organisation yet.</p>;
  }
  if (!settings) return <p className="text-sm text-gray-500">Loading…</p>;

  const value = (s) => (s.key === "risk_required_for_mh" ? settings[s.key] !== false : Boolean(settings[s.key]));

  const escalation = settings.crisis_admin_escalation_minutes ?? 10;
  const saveEscalation = async (raw) => {
    const minutes = Math.min(120, Math.max(0, parseInt(raw, 10) || 0));
    if (minutes === escalation) return;
    setSaving("crisis_admin_escalation_minutes");
    try {
      await api.updateGeneralSettings({ crisis_admin_escalation_minutes: minutes });
      setSettings((prev) => ({ ...prev, crisis_admin_escalation_minutes: minutes }));
      toast.success(minutes === 0 ? "Admins will be told about every crisis alert straight away" : `Admins will be told if nobody responds within ${minutes} minutes`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save this setting.");
    } finally {
      setSaving(null);
    }
  };

  const checkinDays = settings.goal_checkin_every_days ?? 7;
  const saveCheckin = async (days) => {
    setSaving("goal_checkin_every_days");
    try {
      await api.updateGeneralSettings({ goal_checkin_every_days: days });
      setSettings((prev) => ({ ...prev, goal_checkin_every_days: days }));
      toast.success(days ? `Patients will be asked about their goals every ${days === 7 ? "week" : `${days / 7} weeks`}` : "Patients won't be asked about their goals");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save this setting.");
    } finally {
      setSaving(null);
    }
  };

  const toggle = async (s) => {
    const next = !value(s);
    setSaving(s.key);
    try {
      await api.updateGeneralSettings({ [s.key]: next });
      setSettings((prev) => ({ ...prev, [s.key]: next }));
      toast.success(next ? s.on : s.off);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save this setting.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      {visible.map((s) => (
        <div key={s.key} className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
          <h3 className="text-base md:text-lg font-semibold text-gray-800">{s.title}</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1">{s.help}</p>
          <label className="normal-case font-sans tracking-normal mt-3 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={value(s)} disabled={!isOrgAdmin || saving === s.key} onChange={() => toggle(s)} />
            <span className="text-sm text-gray-700">
              {s.label}
              {!isOrgAdmin && <span className="block text-xs text-gray-500">Only an organisation admin can change this.</span>}
            </span>
          </label>
        </div>
      ))}
      {features.has("treatment_plan") && (
        <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
          <h3 className="text-base md:text-lg font-semibold text-gray-800">Goal check-ins</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Patients with a shared treatment plan say how each goal is going (Not yet / Some days / Most days / Done) in My Care. They answer once per period - answering again in the same period changes that answer - and get one reminder when the next check-in is due. Clinicians see the answers on the plan.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span>Ask patients</span>
            <select value={checkinDays} disabled={!isOrgAdmin || saving === "goal_checkin_every_days"} onChange={(e) => saveCheckin(parseInt(e.target.value, 10))}
              aria-label="How often to ask about goals" className="rounded-md border border-gray-300 bg-white p-1.5 text-sm">
              <option value={7}>every week</option>
              <option value={14}>every 2 weeks</option>
              <option value={28}>every 4 weeks</option>
              <option value={0}>never (patients can still update)</option>
            </select>
          </div>
          {!isOrgAdmin && <p className="mt-1 text-xs text-gray-500">Only an organisation admin can change this.</p>}
        </div>
      )}
      {features.has("crisis") && (
        <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50">
          <h3 className="text-base md:text-lg font-semibold text-gray-800">Crisis alerts: when to tell admins</h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            A crisis alert goes straight to the patient's care team and their supervisors. Organisation admins are told only if nobody taps "I'm on it" within this time, so an alert is never missed. If a patient has no care team yet, admins are told straight away.
          </p>
          <label className="normal-case font-sans tracking-normal mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-700">
            Tell admins after
            <input type="number" min={0} max={120} key={escalation} defaultValue={escalation} disabled={!isOrgAdmin || saving === "crisis_admin_escalation_minutes"}
              onBlur={(e) => saveEscalation(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              onWheel={(e) => e.currentTarget.blur()}
              className="w-20 rounded-md border border-gray-300 p-1.5 text-sm" aria-label="Minutes before admins are told" />
            minutes with no response <span className="text-xs text-gray-500">(0 = tell admins straight away)</span>
          </label>
          {!isOrgAdmin && <p className="mt-1 text-xs text-gray-500">Only an organisation admin can change this.</p>}
        </div>
      )}
    </div>
  );
}
